<template>
  <ConfirmModal persist hide-actions data-testid="iq-edit-modal" @close-dialog="closeModal">
    <template #title>
      <h1 class="p-6 pb-5 text-headline-m">{{ typed_t('ticketForm.editTicketModal') }}</h1>
    </template>
    <template #content>
      <TicketForm
        :invitation-quota="props.invitationQuota"
        :ticket="props.ticket"
        :error-message="errorMessage"
        :payment-info-error="props.paymentInfoError"
        @close-dialog="closeModal"
        @submit-form="submitForm"
      />
    </template>
  </ConfirmModal>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/vue'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useUpdateTicketMutation, useCreatePaymentInfoMutation, useUpdatePaymentInfoMutation, TicketEvent } from '@/gql/myomr'
import type { TicketQuery, InvitationQuotaQuery, DirectTicketAttributes } from '@/gql/myomr'
import { injectStrict } from '@/helpers/injectStrict'
import { showNotification } from '@/helpers/notificationHelper'
import typed_translation from '@/helpers/typed_translation'
import { reloadQuotaKey } from '@/symbols/reloadQuotaKey'
import { reloadTicketsKey } from '@/symbols/reloadTicketsKey'

import type { PaymentInfoData } from './PaymentInfoSection.vue'

type Ticket = NonNullable<TicketQuery['ticket']>

type FormData = DirectTicketAttributes & {
  paymentInfo?: PaymentInfoData
}

const props = defineProps<{
  invitationQuota: InvitationQuotaQuery['invitationQuota']
  paymentInfoError?: string
  ticket: Ticket
}>()

const { t } = useI18n()
const typed_t = typed_translation('InvitationQuota', props.invitationQuota.kind)
const route = useRoute()
const router = useRouter()

const reloadQuota = injectStrict(reloadQuotaKey)
const reloadTickets = injectStrict(reloadTicketsKey)

const { mutate: updateTicket } = useUpdateTicketMutation()
const { mutate: createPaymentInfo } = useCreatePaymentInfoMutation()
const { mutate: updatePaymentInfo } = useUpdatePaymentInfoMutation()
const errorMessage = ref('')

function closeModal() {
  router.push({ name: 'invitationQuota', query: route.query })
}

function showError(message: string) {
  errorMessage.value = message
  showNotification(t('InvitationQuota.CrewQuota.unsuccessfulUpdateNotification'), 'error', 3000)
}

function buildPaymentAttributes(formData: FormData, paymentInfo: PaymentInfoData) {
  return {
    salutation: formData.salutation,
    title: formData.title || undefined,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    country: paymentInfo.country,
    company: paymentInfo.company || undefined,
    vatNumber: paymentInfo.vatNumber || undefined,
    customerReference: paymentInfo.customerReference || undefined,
    zip: paymentInfo.zip,
    city: paymentInfo.city,
    street: paymentInfo.street,
    number: paymentInfo.number,
    additionalAddress: paymentInfo.additionalAddress || undefined,
    business: paymentInfo.business,
  }
}

async function resolvePaymentInfoId(formData: FormData): Promise<string | undefined> {
  if (!formData.paymentInfo) return undefined

  const paymentAttributes = buildPaymentAttributes(formData, formData.paymentInfo)
  const existingId = props.ticket.paymentInfo?.id

  if (existingId) {
    const result = await updatePaymentInfo({ paymentInfoId: existingId, attributes: paymentAttributes })
    const errors = result?.data?.updatePaymentInfo?.errors ?? []
    if (errors.length > 0) {
      showError(errors.join(', '))
      return undefined
    }
    return existingId
  }

  const result = await createPaymentInfo({ attributes: paymentAttributes })
  const errors = result?.data?.createPaymentInfo?.errors ?? []
  if (errors.length > 0) {
    showError(errors.join(', '))
    return undefined
  }

  const newId = result?.data?.createPaymentInfo?.paymentInfo?.id
  if (!newId) {
    showError(t('FestivalShop.checkout.paymentInfoError'))
    return undefined
  }
  return newId
}

async function submitForm(formData: FormData) {
  try {
    const paymentInfoId = await resolvePaymentInfoId(formData)
    if (formData.paymentInfo && !paymentInfoId) return

    const { paymentInfo: _, ...ticketAttributes } = formData
    const response = await updateTicket({
      ticketId: props.ticket.id,
      ticketCreateAttributes: ticketAttributes,
      events: [TicketEvent.Complete],
      paymentInfoId,
    })

    if (response?.data?.updateDirectTicket?.errors?.length) {
      showError(response.data.updateDirectTicket.errors.join(', '))
      return
    }

    errorMessage.value = ''
    reloadQuota()
    reloadTickets()
    closeModal()
    showNotification(t('InvitationQuota.CrewQuota.successfulTicketUpdate'), 'success', 3000)
  } catch (e) {
    captureException(e)
    showNotification(t('InvitationQuota.CrewQuota.unsuccessfulUpdateNotification'), 'error', 3000)
  }
}
</script>
